import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Pie, Line } from "react-chartjs-2";
import GaugeChart from "react-gauge-chart";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import ApexChart from "react-apexcharts";
import config from '../config.js'; 
import { parseDueDate } from '../utils/dueDate';
// The reads, date helpers and card rules are shared with the phone Dashboard
// (features/dashboard/dashboardData.js, docs/mobile-pwa-plan.md "One copy of every action").
import {
  STATIONS, formatHoursToHM, parseIntervalToHours, formatDateNoTime, parseDateAsLocal, getWeekdayCount,
  isInLocalDateRange, getUtcRangeForLocalDate, safeFetchJson, loadActiveJobs, jobAlerts, groupByDueDate, materialAlerts, withFlag,
  pendingRfqGroups, stationHoursByDay,
} from '../features/dashboard/dashboardData';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
  ChartDataLabels,
  annotationPlugin
);

// ========================================================================
// HELPER FUNCTIONS
// ========================================================================


// ========================================================================

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Data sets
  const [jobsData, setJobsData] = useState([]);
  const [tasksData, setTasksData] = useState([]);
  const [materialsData, setMaterialsData] = useState([]);
  const [consumablesData, setConsumablesData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [timePunchesData, setTimePunchesData] = useState([]);
  const [cutSheetsData, setCutSheetsData] = useState([]);

  // Summary counts
  const [jobsCount, setJobsCount] = useState(0);
  const [tasksCount, setTasksCount] = useState(0);
  const [materialsCount, setMaterialsCount] = useState(0);
  const [consumablesCount, setConsumablesCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);

  // Workstation capacity (from tasks' scheduled_date)
  const [capacityByStation, setCapacityByStation] = useState({});

  // Priority & Late & DueToday
  const [priorityOneJobs, setPriorityOneJobs] = useState([]);
  const [lateJobs, setLateJobs] = useState([]);
  const [dueTodayJobs, setDueTodayJobs] = useState([]);

  // Consumable reorder / Material negative availability
  const [consumablesReorder, setConsumablesReorder] = useState([]);
  const [materialsNegative, setMaterialsNegative] = useState([]);

  // User leaderboard
  const [userLeaderboard, setUserLeaderboard] = useState([]);

  // For the top-5 usage chart
  const [topUsageMaterials, setTopUsageMaterials] = useState([]);

  // Flagged items
  const [flaggedParts, setFlaggedParts] = useState([]);
  const [flaggedCutSheets, setFlaggedCutSheets] = useState([]);

  const SHIFT_HOURS = 7;

  const [capacityStartDate, setCapacityStartDate] = useState(() => formatDateNoTime(new Date()));
  const [capacityEndDate, setCapacityEndDate] = useState(() => formatDateNoTime(new Date()));

  const [reportDate, setReportDate] = useState(() => formatDateNoTime(new Date()));

  const [stationActivity, setStationActivity] = useState([]);
  const [userTimes, setUserTimes] = useState([]);
  const [jobsShipped, setJobsShipped] = useState([]);
  const [laserUsage, setLaserUsage] = useState([]);
  const [scheduleBumps, setScheduleBumps] = useState([]);

  const [leaderboardStart, setLeaderboardStart] = useState(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    return formatDateNoTime(sevenDaysAgo);
  });
  const [leaderboardEnd, setLeaderboardEnd] = useState(() => formatDateNoTime(new Date()));

  const [dailyStationHours, setDailyStationHours] = useState({});

  const [selectedWorkstation, setSelectedWorkstation] = useState("Laser");
  const [completedJobsDate, setCompletedJobsDate] = useState(() => formatDateNoTime(new Date()));
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loadingCompletedJobs, setLoadingCompletedJobs] = useState(false);

  // Timeline chart state
  const [timelineStation, setTimelineStation] = useState("Laser");
  const [timelineData, setTimelineData] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Pending RFQs
  const [pendingRfqs, setPendingRfqs] = useState([]);

  const totalCompletedHours = completedJobs.reduce((sum, job) => sum + (job.total_time_seconds || 0), 0) / 3600;

  const navigate = useNavigate();
  const API_BASE = config.API_BASE_URL;

  useEffect(() => { fetchAllData(); }, []);
  useEffect(() => { if (tasksData.length > 0) computeCapacityInRange(tasksData, capacityStartDate, capacityEndDate); }, [tasksData, capacityStartDate, capacityEndDate]);
  useEffect(() => { if (timePunchesData.length > 0 && tasksData.length > 0) computeDailyStationHoursFromPunches(timePunchesData, tasksData, capacityStartDate, capacityEndDate); }, [timePunchesData, tasksData, capacityStartDate, capacityEndDate]);
  useEffect(() => {
    fetchDailyProductivity(reportDate);
    fetchStationActivity(reportDate);
    fetchUserTimes(reportDate);
  }, [reportDate]);
  useEffect(() => { if (usersData.length > 0 && tasksData.length > 0 && timePunchesData.length > 0) deriveUserLeaderboard(usersData, tasksData, timePunchesData, leaderboardStart, leaderboardEnd); }, [usersData, tasksData, timePunchesData, leaderboardStart, leaderboardEnd]);
  useEffect(() => { if (selectedWorkstation && completedJobsDate) fetchCompletedJobs(selectedWorkstation, completedJobsDate); }, [selectedWorkstation, completedJobsDate]);
  useEffect(() => { if (timelineStation && reportDate) fetchTimelineData(timelineStation, reportDate); }, [timelineStation, reportDate]);


  async function fetchAllData() {
    setLoading(true);
    setError("");
    try {
      // Fetch each endpoint independently so one failure doesn't kill the whole dashboard
      const safeFetch = safeFetchJson;

      const [jobsJson, tasksJson, matsJson, consJson, usersJson, punchesJson, csJson, partsJson] = await Promise.all([
        // Job fields only (summary rows), page by page: GET /jobs in one response can pass
        // Lambda's 6 MB limit (utils/jobPages.js). Same fallback as safeFetch: [] on failure.
        loadActiveJobs(),
        safeFetch(`${API_BASE}/job_tasks?days=90`, "job_tasks"),
        safeFetch(`${API_BASE}/materials`, "materials"),
        safeFetch(`${API_BASE}/consumables`, "consumables"),
        safeFetch(`${API_BASE}/users`, "users"),
        safeFetch(`${API_BASE}/time_punches?days=30&include_workstation=true&exclude_maintenance=true`, "time_punches"),
        safeFetch(`${API_BASE}/cut_sheets`, "cut_sheets"),
        safeFetch(`${API_BASE}/parts`, "parts"),
      ]);

      setJobsData(jobsJson);
      setJobsCount(jobsJson.length);
      setTasksData(tasksJson);
      setTasksCount(tasksJson.length);
      setMaterialsData(matsJson);
      setMaterialsCount(matsJson.length);
      setConsumablesData(consJson);
      setConsumablesCount(consJson.length);
      setUsersData(usersJson);
      setUsersCount(usersJson.length);
      setTimePunchesData(punchesJson);

      const sheetsWithFlag = withFlag(csJson);
      setCutSheetsData(sheetsWithFlag);
      setFlaggedCutSheets(sheetsWithFlag.filter(c => c.flagged));

      setFlaggedParts(withFlag(partsJson).filter(p => p.flagged));

      deriveJobAlerts(jobsJson);
      deriveMaterialAlerts(matsJson, consJson);
      computeSheetUsage(tasksJson, sheetsWithFlag, matsJson);

      // Fetch pending RFQs
      try {
        const rfqJson = await safeFetch(`${API_BASE}/purchase_orders?status=RFQ`, "rfqs");
        setPendingRfqs(pendingRfqGroups(rfqJson));
      } catch (rfqErr) {
        console.warn("Failed to fetch pending RFQs:", rfqErr);
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDailyProductivity(dateStr) {
    try {
      const url = `${API_BASE}/reports/daily_productivity?date_str=${dateStr}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch daily_productivity (${res.status})`);
      const json = await res.json();
      setJobsShipped(json.jobs_shipped || []);
      setLaserUsage(json.laser_usage || []);
      setScheduleBumps(json.schedule_bumps || []);
    } catch (e) {
      console.error("fetchDailyProductivity error:", e);
    }
  }

  async function fetchStationActivity(dateStr) {
    try {
      const url = `${API_BASE}/reports/station_activity?date_str=${dateStr}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch station_activity (${res.status})`);
      const json = await res.json();
      setStationActivity(json || []);
    } catch (e) {
      console.error("fetchStationActivity error:", e);
    }
  }

  async function fetchUserTimes(dateStr) {
    try {
      const url = `${API_BASE}/reports/user_times?date_str=${dateStr}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch user_times (${res.status})`);
      const data = await res.json();
      setUserTimes(data || []);
    } catch (err) {
      console.error("fetchUserTimes error:", err);
      setUserTimes([]);
    }
  }

  async function fetchCompletedJobs(workstation, dateStr) {
    setLoadingCompletedJobs(true);
    try {
      const url = `${API_BASE}/reports/completed_by_station?workstation=${workstation}&date_str=${dateStr}`;
      const res = await fetch(url);
      if (!res.ok) {
        console.error(`Endpoint for completed jobs might not exist. Status: ${res.status}`);
        setCompletedJobs([]);
        return;
      }
      const data = await res.json();
      setCompletedJobs(data || []);
    } catch (err) {
      console.error("fetchCompletedJobs error:", err);
      setCompletedJobs([]);
    } finally {
      setLoadingCompletedJobs(false);
    }
  }

  async function fetchTimelineData(station, dateStr) {
    setLoadingTimeline(true);
    try {
      const url = `${API_BASE}/stations/${station}/timeline/${dateStr}`;
      const res = await fetch(url);
      if (!res.ok) {
        console.error(`Failed to fetch timeline data for ${station}. Status: ${res.status}`);
        setTimelineData([]);
        return;
      }
      const data = await res.json();
      setTimelineData(data || []);
    } catch (err) {
      console.error("fetchTimelineData error:", err);
      setTimelineData([]);
    } finally {
      setLoadingTimeline(false);
    }
  }

  function computeCapacityInRange(tasksJson, startDateStr, endDateStr) {
    const usageHours = STATIONS.reduce((acc, st) => ({ ...acc, [st]: 0 }), {});
    tasksJson.forEach(t => {
      if (t.workstation && STATIONS.includes(t.workstation) && isInLocalDateRange(t.scheduled_date, startDateStr, endDateStr)) {
        usageHours[t.workstation] += parseIntervalToHours(t.estimated_time);
      }
    });
    const weekdayCount = getWeekdayCount(startDateStr, endDateStr);
    const capacityHours = weekdayCount * SHIFT_HOURS;
    const capacityObj = STATIONS.reduce((acc, st) => {
      const totalH = usageHours[st] || 0;
      let ratio = capacityHours > 0 ? totalH / capacityHours : 0;
      acc[st] = { ratio: Math.max(0, ratio), hours: +totalH.toFixed(1) };
      return acc;
    }, {});
    setCapacityByStation(capacityObj);
  }

  function computeDailyStationHoursFromPunches(punchesJson, tasksJson, startDateStr, endDateStr) {
    // Station hours use wall-clock time (no multiplier): features/dashboard stationHoursByDay.
    setDailyStationHours(stationHoursByDay(punchesJson, tasksJson, startDateStr, endDateStr, STATIONS));
  }

  function deriveJobAlerts(jobsJson) {
    const a = jobAlerts(jobsJson);
    setPriorityOneJobs(a.priorityJobs);
    setLateJobs(a.lateJobs);
    setDueTodayJobs(a.dueTodayJobs);
  }

  function deriveMaterialAlerts(matsJson, consJson) {
    const a = materialAlerts(matsJson, consJson);
    setConsumablesReorder(a.consumablesReorder);
    setMaterialsNegative(a.materialsNegative);
  }

  function deriveUserLeaderboard(usersJson, tasksJson, punchesJson, startDateStr, endDateStr) {
      const [startUtc, endUtc] = getUtcRangeForLocalDate(startDateStr, endDateStr);
      const filteredPunches = punchesJson.filter(p => {
          const pt = new Date(p.punch_time);
          return pt >= startUtc && pt <= endUtc;
      });
      const userStats = usersJson.reduce((acc, u) => {
          acc[u.user_id] = { name: u.name, totalHours: 0, jobsCompleted: 0 };
          return acc;
      }, {});
      const punchesByTask = filteredPunches.reduce((acc, p) => {
          (acc[p.task_id] = acc[p.task_id] || []).push(p);
          return acc;
      }, {});
      Object.values(punchesByTask).forEach(taskPunches => {
          taskPunches.sort((a, b) => new Date(a.punch_time) - new Date(b.punch_time));
          // Get machine_multiplier from punch data (API returns it with include_workstation=true)
          const multiplier = taskPunches[0]?.machine_multiplier || 1;
          let curStart = null, curUser = null;
          taskPunches.forEach(p => {
              const pTime = new Date(p.punch_time);
              if (p.punch_type === "start" || p.punch_type === "resume") {
                  curStart = pTime;
                  curUser = p.user_id;
              } else if ((p.punch_type === "pause" || p.punch_type === "complete") && curStart && curUser && userStats[curUser]) {
                  userStats[curUser].totalHours += ((pTime - curStart) / 3600000) * multiplier;
                  if (p.punch_type === "complete") {
                      userStats[curUser].jobsCompleted++;
                  }
                  curStart = null;
              }
          });
      });
      const finalArr = Object.entries(userStats)
          .map(([user_id, stats]) => ({ user_id, ...stats, totalHours: +stats.totalHours.toFixed(2) }))
          .filter(u => u.totalHours > 0 || u.jobsCompleted > 0)
          .sort((a, b) => b.totalHours - a.totalHours);
      setUserLeaderboard(finalArr);
  }

  function computeSheetUsage(tasksJson, csJson, matsJson) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const csMap = csJson.reduce((acc, cs) => {
          if (cs.cut_sheet_id && cs.material_id) acc[cs.cut_sheet_id] = cs.material_id;
          return acc;
      }, {});
      const matMap = matsJson.reduce((acc, m) => {
          acc[m.material_id] = m;
          return acc;
      }, {});
      const usage = tasksJson.reduce((acc, t) => {
          const scheduledDate = t.scheduled_date ? new Date(t.scheduled_date) : null;
          if (t.workstation === "Laser" && t.cut_sheet_id && t.sheet_count > 0 && scheduledDate && scheduledDate >= sevenDaysAgo) {
              const mid = csMap[t.cut_sheet_id];
              if (mid) acc[mid] = (acc[mid] || 0) + t.sheet_count;
          }
          return acc;
      }, {});
      const arr = Object.entries(usage).map(([mid, count]) => {
          const mat = matMap[mid];
          const label = mat ? (mat.material_code || mat.description || `Material #${mid}`) : `Material #${mid}`;
          return { material_id: parseInt(mid, 10), label, usedSheets: count };
      });
      arr.sort((a, b) => b.usedSheets - a.usedSheets);
      setTopUsageMaterials(arr.slice(0, 5));
  }
  
  if (loading) return <div>Loading Dashboard...</div>;
  if (error) return <div className="alert alert-danger">Error: {error}</div>;

  const usageBarData = {
    labels: topUsageMaterials.map(x => x.label),
    datasets: [{
      label: "Sheets Consumed (Last 7 Days)",
      data: topUsageMaterials.map(x => x.usedSheets),
      backgroundColor: "rgba(54, 162, 235, 0.6)",
    }],
  };
  const statusCounts = jobsData.reduce((acc, j) => {
    const st = j.status || "Unknown";
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});
  const pieData = {
    labels: Object.keys(statusCounts),
    datasets: [{
      label: "# of Jobs",
      data: Object.values(statusCounts),
      backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#AAA", "#666"],
    }],
  };
  const monthlyJobCounts = jobsData.reduce((acc, j) => {
    if (j.due_date) {
      const bucket = j.due_date.substring(0, 7);
      acc[bucket] = (acc[bucket] || 0) + 1;
    }
    return acc;
  }, {});
  const sortedMonths = Object.keys(monthlyJobCounts).sort();
  const lineData = {
    labels: sortedMonths,
    datasets: [{
      label: "Jobs by Due Month",
      data: sortedMonths.map(m => monthlyJobCounts[m]),
      borderColor: "rgba(75, 192, 192, 1)",
      fill: false,
    }],
  };
    
  const stationStackData = stationActivity.map(s => ({
    station: s.station,
    working: +(s.working_seconds / 3600).toFixed(2),
    waiting: +(s.waiting_seconds / 3600).toFixed(2),
    idle: +(s.idle_seconds / 3600).toFixed(2),
    maintenance: +(s.maintenance_seconds / 3600).toFixed(2),
  }));

  const userTimeData = userTimes.reduce((acc, ut) => {
      const st = ut.workstation || "Unassigned";
      if (!acc.userGrouping[ut.user_id]) {
          acc.userGrouping[ut.user_id] = { userName: ut.user_name, totalSec: 0, stations: {} };
      }
      acc.workstationSet.add(st);
      acc.userGrouping[ut.user_id].totalSec += ut.total_seconds;
      acc.userGrouping[ut.user_id].stations[st] = (acc.userGrouping[ut.user_id].stations[st] || 0) + ut.total_seconds;
      return acc;
  }, { userGrouping: {}, workstationSet: new Set() });
  const sortedUserTimeEntries = Object.entries(userTimeData.userGrouping).sort(([, a], [, b]) => b.totalSec - a.totalSec);
  const userTimeLabels = sortedUserTimeEntries.map(([, data]) => data.userName);
  const sortedUserTimeStations = Array.from(userTimeData.workstationSet).sort();
  const userTimeStationColors = ["#FF6633", "#FFB399", "#FF33FF", "#FFFF99", "#00B3E6", "#E6B333"];
  const userTimeStackData = {
      labels: userTimeLabels,
      datasets: sortedUserTimeStations.map((st, idx) => ({
          label: st,
          data: sortedUserTimeEntries.map(([, d]) => +((d.stations[st] || 0) / 3600).toFixed(2)),
          backgroundColor: userTimeStationColors[idx % userTimeStationColors.length],
          stack: "userStack",
      })),
  };
  const groupedLateJobs = groupByDueDate(lateJobs);
  const allDaysForLineChart = Object.keys(dailyStationHours).sort();
  const dailyStationLineLabels = allDaysForLineChart.map(dayStr => {
    const d = parseDateAsLocal(dayStr);
    return d ? d.toLocaleDateString("en-US", { weekday: 'short', month: 'numeric', day: '2-digit' }) : dayStr;
  });
  const dailyStationLineData = {
    labels: dailyStationLineLabels,
    datasets: [
      ...STATIONS.map((st, idx) => ({
        label: st,
        data: allDaysForLineChart.map(dayStr => dailyStationHours[dayStr]?.[st] || 0),
        borderColor: userTimeStationColors[idx % userTimeStationColors.length],
        fill: false,
      })),
      {
        label: `Capacity (${SHIFT_HOURS}h Workday)`,
        data: allDaysForLineChart.map(dayStr => {
          const d = parseDateAsLocal(dayStr);
          const dayOfWeek = d ? d.getDay() : -1;
          return (dayOfWeek === 0 || dayOfWeek === 6) ? 0 : SHIFT_HOURS;
        }),
        borderColor: "#000",
        borderDash: [5, 5],
        borderWidth: 2,
        fill: false,
      },
    ],
  };
  const dayCountForGauges = getWeekdayCount(capacityStartDate, capacityEndDate);

  const stateColors = {
    "Working": "#28a745",
    "Idle": "#dc3545",
    "Waiting": "#ffc107",
    "At Risk": "#fd7e14",
    "Paused": "#6f42c1",
    "Maintenance": "#0dcaf0",
  };
  const defaultColor = "#6c757d";
  const apexSeries = [{
    name: 'Status',
    data: timelineData.map(block => ({
      x: timelineStation,
      y: [
        new Date(block.start_time).getTime(),
        new Date(block.end_time).getTime()
      ],
      state: block.state,
      tasks: block.tasks ?? []
    }))
  }];
  const dayStart = parseDateAsLocal(reportDate);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);

  const apexOptions = {
    chart: {
      type: "rangeBar",
      toolbar: {
        show: true, 
      },
      zoom: {
        enabled: true,
        type: 'x', 
        autoScaleYaxis: true
      }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        barHeight: "50%",
      }
    },
    colors: timelineData.map(block => stateColors[block.state] || defaultColor),
    xaxis: {
      type: "datetime",
      min: dayStart.getTime(),
      max: dayEnd.getTime(),
      tickAmount: 12,
      labels: {
        datetimeUTC: false,
        format: 'h tt'
      }
    },
    yaxis: {
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    legend: {
      show: false
    },
    grid: {
      yaxis: {
        lines: { show: false }
      },
      xaxis: {
        lines: { show: true }
      }
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const point = w.config.series[seriesIndex].data[dataPointIndex];
        const { state, tasks = [] } = point;
        const [start, end] = point.y;
        
        const formatTime = t => new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const tasksHtml = tasks.length > 0
          ? `<ul style="margin: 5px 0 0 0; padding-left: 1rem; list-style-type: none;">
              ${tasks.map(t => `
                <li style="margin-top: 8px;">
                  <div><strong>Job:</strong> #${t.master_job_num}</div>
                  <div><strong>Desc:</strong> ${t.job_description || 'N/A'}</div>
                  <div><strong>User:</strong> ${t.user_name || 'N/A'}</div>
                </li>
              `).join("")}
            </ul>`
          : "";

        return `<div class="p-2" style="font-family: sans-serif; font-size: 12px;">
                  <div><strong>${state}</strong>: ${formatTime(start)} – ${formatTime(end)}</div>
                  ${tasksHtml}
                </div>`;
      }
    }
  };


  return (
    <div className="mb-4" style={{ padding: "1rem" }}>
      <h2>Dashboard</h2>

      <div className="d-flex align-items-center mb-3">
        <label className="me-2">Productivity & Timeline Date:</label>
        <input
          type="date"
          className="form-control"
          style={{width: "auto"}}
          value={reportDate}
          onChange={e => setReportDate(e.target.value)}
        />
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card text-bg-warning p-3">
            <h4>Top Priority Jobs (P1 &amp; P2)</h4>
            <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                {priorityOneJobs.length === 0 ? <p>No top-priority jobs</p> : (
                  <ul className="list-group list-group-flush">
                    {priorityOneJobs.map(j => (
                      <li key={j.job_id} className="list-group-item bg-transparent text-dark">
                        #{j.master_job_num}: {j.job_description}
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card text-bg-danger p-3">
            <h4>Late Jobs</h4>
            <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                {lateJobs.length === 0 ? <p>No Late Jobs</p> : (
                  <ul className="list-group list-group-flush">
                    {Object.entries(groupedLateJobs).map(([date, arr]) => (
                      <React.Fragment key={date}>
                        <li className="list-group-item bg-light text-dark"><strong>Due Date: {date}</strong></li>
                        {arr.map(job => (
                          <li key={job.job_id} className="list-group-item bg-transparent text-white">
                            #{job.master_job_num}: {job.job_description}
                          </li>
                        ))}
                      </React.Fragment>
                    ))}
                  </ul>
                )}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card text-bg-info p-3">
            <h4>Jobs Due Today</h4>
             <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                {dueTodayJobs.length === 0 ? <p>No Jobs Due Today</p> : (
                  <ul className="list-group list-group-flush">
                    {dueTodayJobs.map(j => (
                      <li key={j.job_id} className="list-group-item bg-transparent text-dark">
                        #{j.master_job_num}: {j.job_description}
                      </li>
                    ))}
                  </ul>
                )}
             </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-4">
            <div className="card text-bg-primary p-3">
                <h5>Items Requiring Review</h5>
                <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {(flaggedParts.length === 0 && flaggedCutSheets.length === 0) ? (<p>No flagged items.</p>) : (
                        <>
                            {flaggedParts.length > 0 && (
                                <>
                                    <h6>Flagged Parts</h6>
                                    <ul className="list-group list-group-flush">
                                        {flaggedParts.map(part => (
                                            <li key={`part-${part.part_id}`} className="list-group-item bg-transparent text-white">
                                                {part.product_id} - {part.description}
                                                {part.flag_reason && <em className="ms-2">({part.flag_reason})</em>}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                            {flaggedCutSheets.length > 0 && (
                                <>
                                    <h6 className="mt-3">Flagged Cut Sheets</h6>
                                    <ul className="list-group list-group-flush">
                                        {flaggedCutSheets.map(cs => (
                                            <li key={`cs-${cs.cut_sheet_id}`} className="list-group-item bg-transparent text-white">
                                                {cs.cut_sheet_name || "Unnamed"} - {cs.description}
                                                {cs.flag_reason && <em className="ms-2">({cs.flag_reason})</em>}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
        <div className="col-md-4">
            <div className="card text-bg-warning p-3">
                <h5>Consumables Below Reorder Point</h5>
                <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {consumablesReorder.length === 0 ? (<p>No items need reordering.</p>) : (
                        <ul className="list-group list-group-flush">
                            {consumablesReorder.map(c => (
                                <li key={c.consumable_id} className="list-group-item bg-transparent text-dark">
                                    {c.product_id} - Stock: {c.stock_quantity}, RP: {c.reorder_point}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
        <div className="col-md-4">
            <div className="card text-bg-danger p-3">
                <h5>Materials with Negative Availability</h5>
                 <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {materialsNegative.length === 0 ? (<p>No items with negative availability.</p>) : (
                        <ul className="list-group list-group-flush">
                            {materialsNegative.map(m => (
                                <li key={m.material_id} className="list-group-item bg-transparent text-white">
                                    {m.material_code || m.description} → Avail: {(m.stock_quantity || 0) + (m.inbound_quantity || 0) - (m.reserved_quantity || 0)}
                                </li>
                            ))}
                        </ul>
                    )}
                 </div>
            </div>
        </div>
      </div>

      {/* Pending RFQs Card */}
      <div className="row g-3 mb-4">
        <div className="col-12">
          <div className="card p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Pending RFQs <span className="badge bg-warning text-dark ms-2">{pendingRfqs.length}</span></h5>
              <button
                className="btn btn-sm btn-warning"
                onClick={() => navigate('/purchase-orders?action=create-rfq')}
              >
                + Create New RFQ
              </button>
            </div>
            {pendingRfqs.length === 0 ? (
              <p className="text-muted mb-0">No pending RFQs. Create one to start requesting vendor quotes.</p>
            ) : (
              <div className="table-responsive" style={{ maxHeight: "250px", overflowY: "auto" }}>
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Created</th>
                      <th>Vendors</th>
                      <th>Items</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRfqs.map((rfq) => (
                      <tr key={rfq.rfq_group_id}>
                        {/* order_date is a plain date: a calendar day, never an instant (a day early in Central). */}
                        <td>{parseDueDate(rfq.created_at)?.toLocaleDateString() || 'N/A'}</td>
                        <td>
                          <span className="badge bg-secondary me-1">{rfq.vendors.length} vendor{rfq.vendors.length !== 1 ? 's' : ''}</span>
                          <small className="text-muted">{rfq.vendors.join(', ')}</small>
                        </td>
                        <td>{rfq.line_count} item{rfq.line_count !== 1 ? 's' : ''}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate('/purchase-orders')}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {Object.entries({Jobs: jobsCount, Tasks: tasksCount, Materials: materialsCount, Consumables: consumablesCount, Users: usersCount}).map(([title, count]) => (
            <div className="col" key={title}>
                <div className="card p-3 text-center">
                    <h5>{title}</h5>
                    <p className="fs-4 mb-0">{count}</p>
                </div>
            </div>
        ))}
      </div>


      <div className="card p-3 mb-4">
          <h4>Workstation Capacity Gauges</h4>
          <div className="d-flex align-items-center mb-2">
            <label className="me-2">Start:</label>
            <input type="date" value={capacityStartDate} onChange={e => setCapacityStartDate(e.target.value)} className="form-control me-3" style={{ width: "auto" }} />
            <label className="me-2">End:</label>
            <input type="date" value={capacityEndDate} onChange={e => setCapacityEndDate(e.target.value)} className="form-control" style={{ width: "auto" }} />
          </div>
          <div className="row g-3 mt-1">
            {STATIONS.map(station => {
              const stationData = capacityByStation[station] || { ratio: 0, hours: 0 };
              const { ratio, hours } = stationData;
              const gaugePercent = Math.min(ratio, 1);
              const displayPct = (ratio * 100).toFixed(0);
              const avgHrsPerDay = dayCountForGauges > 0 ? (hours / dayCountForGauges) : 0;
              return (
                <div key={station} className="col-lg-2 col-md-4 col-sm-6">
                  <div className="card p-2">
                    <h6 className="text-center">{station}</h6>
                    <GaugeChart id={`gauge-${station}`} nrOfLevels={20} percent={gaugePercent} textColor="#333" formatTextValue={() => `${displayPct}%`} />
                    <p className="text-center mb-0 small">{formatHoursToHM(hours)} | {formatHoursToHM(avgHrsPerDay)}/day</p>
                  </div>
                </div>
              );
            })}
          </div>
      </div>

      <div className="card p-3 mb-4">
        <h5>Daily Workstation Hours (vs {SHIFT_HOURS}h Shift)</h5>
        {dailyStationLineLabels.length === 0 ? (<p>No data available for this date range.</p>) : (
          <div style={{height:300}}>
            <Line
              data={dailyStationLineData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    title: { display: true, text: "Hours" },
                    ticks: {
                        callback: function(value) {
                            return formatHoursToHM(value);
                        }
                    }
                  }
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                          label += ': ';
                        }
                        if (context.parsed.y !== null) {
                          label += formatHoursToHM(context.parsed.y);
                        }
                        return label;
                      }
                    }
                  },
                  datalabels: {
                    formatter: (value) => {
                      if (value > 0) {
                        return formatHoursToHM(value);
                      }
                      return null;
                    },
                    align: 'end',
                    anchor: 'end',
                    color: '#555',
                    font: {
                      size: 10,
                      weight: 'bold'
                    }
                  }
                }
              }}
            />
          </div>
        )}
      </div>
      
      <div className="row g-3 mb-4">
        <div className="col-md-8">
            <div className="card p-3 h-100">
              <h5>Station Activity for {reportDate}</h5>
              {stationActivity.length === 0 ? (<p>No station activity data for this day.</p>) : (
                <div style={{height:300}}>
                  <Bar 
                    data={{
                      labels: stationStackData.map(s => s.station),
                      datasets: [
                          { label: "Working", data: stationStackData.map(s => s.working), backgroundColor: "#82ca9d", stack: "a" },
                          { label: "Waiting", data: stationStackData.map(s => s.waiting), backgroundColor: "#8884d8", stack: "a" },
                          { label: "Idle", data: stationStackData.map(s => s.idle), backgroundColor: "#ffc658", stack: "a" },
                          { label: "Maintenance", data: stationStackData.map(s => s.maintenance), backgroundColor: "#a464c2", stack: "a" }
                      ]
                    }} 
                    options={{ 
                      responsive: true, 
                      maintainAspectRatio: false, 
                      scales: { 
                        x: { stacked: true }, 
                        y: { 
                          stacked: true,
                          ticks: {
                            callback: function(value) {
                                return formatHoursToHM(value);
                            }
                          }
                        } 
                      },
                      plugins: {
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              let label = context.dataset.label || '';
                              if (label) {
                                label += ': ';
                              }
                              if (context.parsed.y !== null) {
                                label += formatHoursToHM(context.parsed.y);
                              }
                              return label;
                            }
                          }
                        },
                        datalabels: {
                          formatter: (value) => {
                            if (value > 0) {
                              return formatHoursToHM(value);
                            }
                            return null;
                          },
                          color: '#000',
                          font: {
                            weight: 'bold',
                            size: 12,
                          }
                        }
                      }
                    }} 
                  />
                </div>
              )}
            </div>
        </div>
        
        <div className="col-md-4">
        <div className="card p-3 h-100">
          <h5>State Timeline for {timelineStation}</h5>

          <select
            className="form-select form-select-sm mb-2"
            value={timelineStation}
            onChange={e => setTimelineStation(e.target.value)}
          >
            {STATIONS.map(s => <option key={s}>{s}</option>)}
          </select>

          {loadingTimeline ? (
            <p>Loading timeline…</p>
          ) : (
            <div style={{ flexGrow: 1, minHeight: 250 }}>
              {apexSeries[0]?.data.length > 0 ? (
                <>
                  <div className="d-flex flex-wrap justify-content-center small gap-3 mb-2">
                    {Object.entries(stateColors).map(([state, color]) => (
                      <div key={state} className="d-flex align-items-center">
                        <span
                          style={{
                            width: "12px",
                            height: "12px",
                            backgroundColor: color,
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                          }}
                        ></span>
                        {state}
                      </div>
                    ))}
                  </div>

                  <ApexChart
                    options={apexOptions}
                    series={apexSeries}
                    type="rangeBar"
                    height={250}
                  />
                </>
              ) : (
                <p className="text-center">No timeline data for this day.</p>
              )}
            </div>
          )}
        </div>
        </div>
      </div>

      <div className="card p-3 mb-4">
          <h5>User Leaderboard</h5>
          <div className="d-flex align-items-center mb-3">
              <label className="me-2">Start:</label>
              <input type="date" className="form-control" style={{width: "auto"}} value={leaderboardStart} onChange={e => setLeaderboardStart(e.target.value)} />
              <label className="ms-3 me-2">End:</label>
              <input type="date" className="form-control" style={{width: "auto"}} value={leaderboardEnd} onChange={e => setLeaderboardEnd(e.target.value)} />
          </div>
          <div className="table-responsive">
              <table className="table table-sm">
                  <thead>
                      <tr><th>User</th><th>Total Hours</th><th>Tasks Completed</th></tr>
                  </thead>
                  <tbody>
                      {userLeaderboard.map(u => (
                          <tr key={u.user_id}>
                            <td>{u.name}</td>
                            <td>{formatHoursToHM(u.totalHours)}</td>
                            <td>{u.jobsCompleted}</td>
                          </tr>
                      ))}
                  </tbody>
              </table>
          </div>
      </div>

      <div className="card p-3 mb-4">
        <h5>User Productivity for {reportDate} (Total Time Worked)</h5>
        {userTimeLabels.length === 0 ? (<p>No data for {reportDate}.</p>) : (
          <div style={{height:300}}>
            <Bar 
              data={userTimeStackData} 
              options={{ 
                responsive: true, 
                maintainAspectRatio: false, 
                scales: { 
                  x: { stacked: true }, 
                  y: { 
                    stacked: true, 
                    title: { display: true, text: "Hours" },
                    ticks: {
                      callback: function(value) {
                        return formatHoursToHM(value);
                      }
                    }
                  }
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) { label += ': '; }
                        if (context.parsed.y !== null) {
                          label += formatHoursToHM(context.parsed.y);
                        }
                        return label;
                      }
                    }
                  },
                  datalabels: {
                    formatter: (value) => {
                      if (value > 0) {
                        return formatHoursToHM(value);
                      }
                      return null;
                    },
                    color: '#000',
                    font: {
                      weight: 'bold',
                      size: 12,
                    }
                  }
                }
              }} 
            />
          </div>
        )}
      </div>
      
      <div className="card p-3 mb-4">
        <h5>Completed Jobs by Station on {completedJobsDate}</h5>
        <div className="d-flex align-items-center mb-3">
            <div className="me-3">
                <label className="form-label me-2">Workstation:</label>
                <select className="form-select" value={selectedWorkstation} onChange={(e) => setSelectedWorkstation(e.target.value)}>
                    {STATIONS.map((station) => (<option key={station} value={station}>{station}</option>))}
                </select>
            </div>
            <div>
                <label className="form-label me-2">Date:</label>
                <input type="date" className="form-control" value={completedJobsDate} onChange={(e) => setCompletedJobsDate(e.target.value)} />
            </div>
        </div>
        {loadingCompletedJobs ? (<p>Loading completed jobs...</p>) : (
            <table className="table table-sm table-striped">
                <thead>
                    <tr><th>Job #</th><th>Description</th><th>User</th><th>Time</th></tr>
                </thead>
                <tbody>
                    {completedJobs.length > 0 ? (
                        completedJobs.map((job, index) => (
                            <tr key={`${job.master_job_num}-${index}`}>
                                <td>{job.master_job_num}</td>
                                <td>{job.job_description}</td>
                                <td>{job.user_name}</td>
                                <td>{formatHoursToHM(job.total_time_seconds / 3600)}</td>
                            </tr>
                        ))
                    ) : (
                        <tr><td colSpan="4" className="text-center">No completed jobs found for the selected criteria.</td></tr>
                    )}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan="3" className="text-end border-top-0"><strong>Total Time</strong></td>
                        <td className="border-top-0"><strong>{formatHoursToHM(totalCompletedHours)}</strong></td>
                    </tr>
                </tfoot>
            </table>
        )}
      </div>

      <div className="card p-3 mb-4">
        <h5>Jobs Shipped on {reportDate}</h5>
        {jobsShipped.length === 0 ? (<p>No jobs were shipped on {reportDate}.</p>) : (
          jobsShipped.map(job => (
            <div key={job.job_id} className="border p-2 mb-2">
              <h6>Job ID: {job.job_id}</h6>
              {job.parts_completed && job.parts_completed.length > 0 ? (
                <ul className="list-group">
                  {job.parts_completed.map(pc => (
                    <li key={pc.part_id} className="list-group-item">
                      Part {pc.part_id}, {pc.product_id} ({pc.description}) → Qty shipped: {pc.qty_shipped}
                    </li>
                  ))}
                </ul>
              ) : (<p>No part-level data.</p>)}
            </div>
          ))
        )}
      </div>

      <div className="card p-3 mb-4">
          <h5>Laser Usage on {reportDate}</h5>
          {laserUsage.length === 0 ? (<p>No laser usage for {reportDate}.</p>) : (
              <ul className="list-group">
                  {laserUsage.map(lu => (
                      <li key={lu.material_id} className="list-group-item">
                          Material: {lu.material_description || `ID ${lu.material_id}`}, Total Sheets Used: {lu.total_sheets_used}
                      </li>
                  ))}
              </ul>
          )}
      </div>

      <div className="card p-3 mb-4">
        <h5>Job Status Distribution</h5>
        <div style={{height:300, width: '100%', maxWidth: 500, margin: 'auto'}}>
          <Pie data={pieData} options={{ responsive: true, maintainAspectRatio: false }} />
        </div>
      </div>

      <div className="card p-3 mb-4">
        <h5>Jobs Over Time (by Due Month)</h5>
        <div style={{height:300}}>
          <Line data={lineData} options={{ responsive: true, maintainAspectRatio: false }} />
        </div>
      </div>

      <div className="card p-3 mb-4">
        <h5>Schedule Bumps on {reportDate}</h5>
        {scheduleBumps.length === 0 ? (<p>No tasks were bumped from {reportDate} due to time constraints.</p>) : (
          <table className="table table-sm">
            <thead>
              <tr><th>Job</th><th>Task</th><th>Station</th><th>Status</th><th>Description</th><th>Bumped At</th></tr>
            </thead>
            <tbody>
              {scheduleBumps.map((b) => (
                <tr key={b.alert_id}>
                  <td>{b.job_id}</td><td>{b.task_id}</td><td>{b.station}</td><td>{b.status}</td><td>{b.description}</td><td>{b.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}